/**
 * Device-only place drafts (spec §8.5 P0).
 *
 * Drafts are persisted in localStorage under 'rust-places-drafts' so form data
 * (and small photos as data-URLs) survive a reload when a cloud save has not
 * been confirmed. A draft is NEVER presented as cloud-saved; the UI labels it
 * 'Alleen op dit apparaat' until repositories.places confirms the write.
 */
import type { SyncSaveState } from '@/types'

export const PLACES_DRAFTS_STORAGE_KEY = 'rust-places-drafts'
/** Draft key for a place that has no server id yet. */
export const NEW_PLACE_DRAFT_KEY = 'new'

export type PlaceDraft = {
  /** Server id when editing an existing place; null for a new place. */
  placeId: string | null
  objectLabel: string
  category: string
  subzone: string
  exactLocation: string
  notes: string
  /** Small photos only (≤ DRAFT_EMBED_MAX_BYTES); null when not embeddable. */
  imageDataUrl: string | null
  state: SyncSaveState
  updatedAt: string
}

export type PlaceDraftMap = Record<string, PlaceDraft>

export function draftKeyFor(placeId: string | null): string {
  return placeId ?? NEW_PLACE_DRAFT_KEY
}

function storageAvailable(): boolean {
  return typeof localStorage !== 'undefined'
}

const VALID_STATES: readonly SyncSaveState[] = [
  'local_saved',
  'sync_queued',
  'syncing',
  'cloud_saved',
  'sync_failed',
]

function isValidDraft(value: unknown): value is PlaceDraft {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    (typeof candidate.placeId === 'string' || candidate.placeId === null) &&
    typeof candidate.objectLabel === 'string' &&
    typeof candidate.category === 'string' &&
    typeof candidate.subzone === 'string' &&
    typeof candidate.exactLocation === 'string' &&
    typeof candidate.notes === 'string' &&
    (typeof candidate.imageDataUrl === 'string' || candidate.imageDataUrl === null) &&
    typeof candidate.state === 'string' &&
    VALID_STATES.includes(candidate.state as SyncSaveState) &&
    typeof candidate.updatedAt === 'string'
  )
}

export function loadPlaceDrafts(): PlaceDraftMap {
  if (!storageAvailable()) return {}
  try {
    const raw = localStorage.getItem(PLACES_DRAFTS_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const drafts: PlaceDraftMap = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (isValidDraft(value)) drafts[key] = value
    }
    return drafts
  } catch {
    // Corrupt or unreadable storage: start clean rather than crashing the page.
    return {}
  }
}

export function writePlaceDrafts(drafts: PlaceDraftMap): void {
  if (!storageAvailable()) return
  try {
    localStorage.setItem(PLACES_DRAFTS_STORAGE_KEY, JSON.stringify(drafts))
  } catch {
    // Storage full or blocked: the in-memory draft still protects this session.
  }
}

export function savePlaceDraft(key: string, draft: PlaceDraft): PlaceDraftMap {
  const drafts = loadPlaceDrafts()
  drafts[key] = draft
  writePlaceDrafts(drafts)
  return drafts
}

export function removePlaceDraft(key: string): PlaceDraftMap {
  const drafts = loadPlaceDrafts()
  delete drafts[key]
  writePlaceDrafts(drafts)
  return drafts
}
